import { useEffect, useRef, ReactNode } from 'react'
import { useRouterState } from '@tanstack/react-router'
import { observer } from 'mobx-react-lite'
import LoadingBar, { LoadingBarRef } from 'react-top-loading-bar'

import { env } from '@/env'
import { Toaster, useColorScheme } from '@/components'
import { DialogStack } from '@/components/new/ui'
import { useNavigate } from '@/routes'
import { webAppState } from '@/appState'

import { AppDebugBar } from './AppDebugBar'
import { AppTooltip } from './AppTooltip'
import { ErrorHandler } from './ErrorHandler'

import type { DebugAction } from './AppDebugBar'

const productThemeColors = {
    campai: {
        light: {
            primaryConnect: '#155dfc',
            primaryConnectForeground: '#FFFFFF',
        },
        dark: {
            primaryConnect: '#155dfc',
            primaryConnectForeground: '#FFFFFF',
        },
    },
    wunderpass: {
        light: {
            primaryConnect: '#155dfc',
            primaryConnectForeground: '#FFFFFF',
        },
        dark: {
            primaryConnect: '#155dfc',
            primaryConnectForeground: '#FFFFFF',
        },
    },
} as const

const productTheme =
    productThemeColors[env.VITE_PRODUCT_KEY as keyof typeof productThemeColors] ?? productThemeColors.campai

export type WebAppProps = {
    noLoadingBar?: boolean
    forceDebugBar?: boolean
    debugActions?: DebugAction[]
    children?: ReactNode
}

// Follows the router on its own, as everything in WebApp would render again twice per navigation otherwise
const RoutingLoadingBar = () => {
    const loadingBarRef = useRef<LoadingBarRef | null>(null)
    const loadingTimerId = useRef<ReturnType<typeof setTimeout> | null>(null)
    const isWaitingRouting = useRouterState({ select: (s) => s.status === 'pending' })

    useEffect(() => {
        if (isWaitingRouting) {
            loadingTimerId.current = setTimeout(() => {
                if (loadingTimerId.current) {
                    loadingTimerId.current = null
                    loadingBarRef.current?.continuousStart(30, 500)
                }
            }, 1000)
        } else {
            if (loadingTimerId.current !== null) {
                clearTimeout(loadingTimerId.current)
                loadingTimerId.current = null
            } else {
                loadingBarRef.current?.complete()
            }
        }
    }, [isWaitingRouting])

    return <LoadingBar className="!bg-success-500" shadow={false} ref={loadingBarRef} height={3} />
}

export const WebApp = observer(({ children, noLoadingBar, forceDebugBar, debugActions }: WebAppProps) => {
    const colorScheme = useColorScheme()

    const navigate = useNavigate()

    useEffect(() => {
        const errorHandler = (evt: ErrorEvent) => {
            if (evt.error) {
                const message = evt.error.message
                const code = evt.error.data?.code

                // we do this only in production because in dev we want to let the dev server show the error
                if (import.meta.env.PROD) {
                    navigate({
                        replace: true,
                        to: '/error',
                        search: {
                            message,
                            code,
                        },
                    })
                }
            }
        }

        window.addEventListener('error', errorHandler)

        return () => window.removeEventListener('error', errorHandler)
    })

    useEffect(() => {
        const theme = productTheme[colorScheme]

        document.documentElement.style.setProperty('color-scheme', colorScheme)
        document.documentElement.style.setProperty('--primary-connect', theme.primaryConnect)
        document.documentElement.style.setProperty('--primary-connect-foreground', theme.primaryConnectForeground)
    }, [colorScheme])

    return (
        <ErrorHandler>
            {!noLoadingBar && <RoutingLoadingBar />}
            {children}
            <DialogStack />
            <AppTooltip />
            {webAppState.isDevelopment && (forceDebugBar || webAppState.enableDebugBar) && (
                <AppDebugBar actions={debugActions} />
            )}
            <Toaster />
        </ErrorHandler>
    )
})
